<!--
  ~ Copyright (c) 2023 D2ArmorPicker by Mijago.
  ~
  ~ This program is free software: you can redistribute it and/or modify
  ~ it under the terms of the GNU Affero General Public License as published
  ~ by the Free Software Foundation, either version 3 of the License, or
  ~ (at your option) any later version.
  ~
  ~ This program is distributed in the hope that it will be useful,
  ~ but WITHOUT ANY WARRANTY; without even the implied warranty of
  ~ MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  ~ GNU Affero General Public License for more details.
  ~
  ~ You should have received a copy of the GNU Affero General Public License
  ~ along with this program.  If not, see <http://www.gnu.org/licenses/>.
  -->

<table
  [dataSource]="tableDataSource"
  class="result-table"
  mat-table
  matSort
  matSortActive="Total"
  matSortDirection="desc"
  multiTemplateDataRows>
  <!--- Note that these columns can be defined in any order.
   The actual rendered columns are set as a property on the row definition" -->
  <!-- Name Column -->
  <ng-container matColumnDef="weapon">
    <th
      *matHeaderCellDef
      mat-header-cell
      mat-sort-header="Weapon"
      matTooltip="The weapon Stat of this armor combination."
      >Weapon
    </th>
    <td *matCellDef="let element" class="statColumn" mat-cell
      >{{ element.stats[ArmorStat.StatWeapon] }}
      <app-stat-icon [stat]="ArmorStat.StatWeapon" class="statIcon"></app-stat-icon>
    </td>
  </ng-container>

  <!-- Name Column -->
  <ng-container matColumnDef="health">
    <th
      *matHeaderCellDef
      mat-header-cell
      mat-sort-header="Health"
      matTooltip="The  stat of this armor combination.">
      Health
    </th>
    <td *matCellDef="let element" class="statColumn" mat-cell
      >{{ element.stats[ArmorStat.StatHealth] }}
      <app-stat-icon [stat]="ArmorStat.StatHealth" class="statIcon"></app-stat-icon>
    </td>
  </ng-container>

  <!-- Name Column -->
  <ng-container matColumnDef="class">
    <th
      *matHeaderCellDef
      mat-header-cell
      mat-sort-header="Class"
      matTooltip="The class stat of this armor combination.">
      Class
    </th>
    <td *matCellDef="let element" class="statColumn" mat-cell
      >{{ element.stats[ArmorStat.StatClass] }}
      <app-stat-icon [stat]="ArmorStat.StatClass" class="statIcon"></app-stat-icon>
    </td>
  </ng-container>

  <!-- Name Column -->
  <ng-container matColumnDef="grenade">
    <th
      *matHeaderCellDef
      mat-header-cell
      mat-sort-header="Grenade"
      matTooltip="The grenade stat of this armor combination.">
      Grenade
    </th>
    <td *matCellDef="let element" class="statColumn" mat-cell
      >{{ element.stats[ArmorStat.StatGrenade] }}
      <app-stat-icon [stat]="ArmorStat.StatGrenade" class="statIcon"></app-stat-icon>
    </td>
  </ng-container>

  <!-- Name Column -->
  <ng-container matColumnDef="super">
    <th
      *matHeaderCellDef
      mat-header-cell
      mat-sort-header="Super"
      matTooltip="The super stat of this armor combination.">
      Super
    </th>
    <td *matCellDef="let element" class="statColumn" mat-cell
      >{{ element.stats[ArmorStat.StatSuper] }}
      <app-stat-icon [stat]="ArmorStat.StatSuper" class="statIcon"></app-stat-icon>
    </td>
  </ng-container>

  <!-- Name Column -->
  <ng-container matColumnDef="melee">
    <th
      *matHeaderCellDef
      mat-header-cell
      mat-sort-header="Melee"
      matTooltip="The melee stat of this armor combination."
      >Melee
    </th>
    <td *matCellDef="let element" class="statColumn" mat-cell
      >{{ element.stats[ArmorStat.StatMelee] }}
      <app-stat-icon [stat]="ArmorStat.StatMelee" class="statIcon"></app-stat-icon>
    </td>
  </ng-container>

  <!-- Name Column -->
  <ng-container matColumnDef="mods">
    <th
      *matHeaderCellDef
      mat-header-cell
      mat-sort-header="Mods"
      matTooltip="The amount of mods required for each combination. Sorting after this takes the mod cost into account.">
      Used Mods
    </th>
    <td *matCellDef="let element" mat-cell>
      <app-table-mod-display
        [mods]="element.mods"
        [artifice]="element.artifice"
        [tuningMods]="element.tuningMods"
        class="modPreview"></app-table-mod-display>
    </td>
  </ng-container>

  <!-- Name Column -->
  <ng-container matColumnDef="exotic">
    <th *matHeaderCellDef mat-header-cell style="text-align: center">Exotic</th>
    <td *matCellDef="let element" mat-cell>
      <div class="itemDiv">
        <img
          *ngIf="element.exotic !== undefined"
          #tooltip="matTooltip"
          class="itemIcon"
          matTooltip="{{ element.exotic.name }}"
          loading="lazy"
          src="https://bungie.net/{{ element.exotic.icon }}" />
        <img
          *ngIf="element.exotic !== undefined"
          class="itemIconWatermark"
          loading="lazy"
          src="https://bungie.net/{{ element.exotic.watermark }}" />
        <img
          *ngIf="element.exotic === undefined"
          class="itemIcon"
          loading="lazy"
          src="https://www.bungie.net/common/destiny2_content/icons/b4d05ef69d0c3227a7d4f7f35bbc2848.png" />
      </div>
    </td>
  </ng-container>

  <!-- Gearset Bonuses Column -->
  <ng-container matColumnDef="gearsetBonuses">
    <th
      *matHeaderCellDef
      mat-header-cell
      mat-sort-header="GearsetBonuses"
      start="desc"
      style="text-align: center">
      Gearset(s)
    </th>
    <td *matCellDef="let element" mat-cell>
      <div class="gearset-bonuses">
        <span
          *ngFor="let bonus of getGearsetBonuses(element)"
          [gearsetTooltip]="bonus.perk"
          class="gearset-bonus">
          <img
            *ngIf="bonus.perk.displayProperties.hasIcon"
            [alt]="bonus.perk.displayProperties.name"
            [src]="'https://bungie.net/' + bonus.perk.displayProperties.icon"
            class="gearset-bonus-icon"
            loading="lazy" />
          <span class="gearset-bonus-count">{{ bonus.requiredSetCount }}</span>
        </span>
        <span *ngIf="getGearsetBonuses(element).length === 0">-</span>
      </div>
    </td>
  </ng-container>

  <!-- Vendor/Collection Column -->
  <ng-container matColumnDef="source">
    <th *matHeaderCellDef mat-header-cell>Sources</th>
    <td *matCellDef="let element" mat-cell>
      <span class="source-column">
        <img
          *ngIf="!!element.usesCollectionRoll"
          matTooltip="This build uses a collection exotic. You have to collect it before you can use it."
          class="collectionIcon"
          src="https://www.bungie.net/common/destiny2_content/icons/1d01287dd47af97fef16fa6acbac23ba.png" />
        <img
          *ngIf="!!element.usesVendorRoll"
          matTooltip="This build uses a vendor item. You have to collect it before you can use it."
          class="vendorIcon"
          src="https://www.bungie.net/common/destiny2_content/icons/8ef4b5bd32277dba9aee7c368404ad5d.jpg" />
      </span>
    </td>
  </ng-container>

  <!-- Name Column -->
  <ng-container matColumnDef="dropdown">
    <th *matHeaderCellDef mat-header-cell></th>
    <td *matCellDef="let element" mat-cell>
      <mat-icon
        *ngIf="!isElementExpanded(element)"
        matTooltip="Click to show details for this build.">
        expand_more
      </mat-icon>
      <mat-icon
        *ngIf="isElementExpanded(element)"
        matTooltip="Click to hide details for this build.">
        expand_less
      </mat-icon>
    </td>
  </ng-container>

  <!-- Total Column -->
  <ng-container matColumnDef="total">
    <th
      *matHeaderCellDef
      mat-header-cell
      mat-sort-header="Total"
      matTooltip="The total sum of all stats in this armor combination."
      >Total
    </th>
    <td *matCellDef="let element" mat-cell>
      {{ getTotalStats(element) }}
    </td>
  </ng-container>

  <!-- Expanded Content Column - The detail row is made up of this one column that spans across all columns -->
  <ng-container matColumnDef="expandedDetail">
    <td *matCellDef="let element" [attr.colspan]="shownColumns.length" mat-cell>
      <div
        [@detailExpand]="isElementExpanded(element) ? 'expanded' : 'collapsed'"
        class="result-element-detail">
        <app-expanded-result-content *ngIf="isElementExpanded(element)" [element]="element">
        </app-expanded-result-content>
      </div>
    </td>
  </ng-container>

  <tr *matHeaderRowDef="shownColumns" mat-header-row></tr>
  <tr
    (click)="toggleElement(element)"
    *matRowDef="let element; columns: shownColumns"
    [class.example-expanded-row]="isElementExpanded(element)"
    class="result-element-row"
    mat-row>
  </tr>
  <tr *matRowDef="let row; columns: ['expandedDetail']" class="result-detail-row" mat-row></tr>
</table>

<mat-paginator
  [pageSizeOptions]="[25, 50, 75, 100]"
  aria-label="Select page of periodic elements"
  pageSize="25"
  showFirstLastButtons>
</mat-paginator>
